// Der Rollenkatalog in der Oberflaeche (ADR-0067): die Rollen lesen, eine
// Rolle vorschlagen, freigeben, umbenennen, beenden und einen Vorschlag
// verwerfen. Welche Aktion an einer Rolle erlaubt ist, sagt der Server je
// Zeile (canApprove/canRename/canEnd).
//
// FACHSPRACHE (NFR-UX-003): "Rolle", "vorschlagen", "freigeben", "vergebbar";
// keine Kennung (role_<uuid>) in der Oberflaeche. Ein Satz je Grundcode mit
// Problem, Wirkung und naechstem Schritt (NFR-UX-005).
//
// VIER AUGEN: den eigenen Vorschlag gibt eine andere Person frei; der Server
// prueft es (context_role_type_second_person_required), die Oberflaeche
// bietet den Knopf am eigenen Vorschlag gar nicht erst an.

import type { PlainMessage } from './access.ts';
import { readResource, sendCommand, type CommandMessages, type CommandOutcome, type SendLike } from './command-client.ts';

/** Abschrift von MAXIMUM_ROLE_NAME_LENGTH (domain/party/context-role-type.ts). */
export const MAXIMUM_ROLE_NAME_LENGTH = 80;
/** Abschrift von MAXIMUM_ROLE_DESCRIPTION_LENGTH (domain/party/context-role-type.ts). */
export const MAXIMUM_ROLE_DESCRIPTION_LENGTH = 500;

export type RoleTypeStatus = 'proposed' | 'active' | 'ended';

export interface RoleTypeView {
  readonly roleCode: string;
  readonly name: string;
  /** Der zuletzt freigegebene Name; `null` bis zur ersten Freigabe. */
  readonly approvedName: string | null;
  readonly description: string | null;
  readonly typeStatus: RoleTypeStatus;
  readonly lockVersion: number;
  readonly proposedByMe: boolean;
  /** Aktive Zuordnungen in den eigenen Haeusern an sichtbaren Kontakten. */
  readonly assignmentCount: number;
  /**
   * Was diese Person an dieser Rolle darf (Schnitt 2): der Server entscheidet,
   * das Web zeigt nur danach Knoepfe. canApprove ist false am eigenen Vorschlag
   * und am eigenen offenen Umbenennen (vier Augen), canEnd false an Startrollen.
   */
  readonly canApprove: boolean;
  readonly canRename: boolean;
  readonly canEnd: boolean;
}

export interface RoleTypePage {
  readonly roleTypes: readonly RoleTypeView[];
  readonly nextCursor: string | undefined;
}

export type RoleTypePageResult = { readonly outcome: 'loaded'; readonly page: RoleTypePage } | { readonly outcome: 'rejected'; readonly message: PlainMessage };

// Kam keine Antwort, weiss der Browser beim Schreiben nicht, ob es gilt; beim Lesen ist nichts passiert.

/** Vorschlagen: dieselbe Kennung beim erneuten Absenden legt nichts doppelt an (ADR-0060). */
export const PROPOSAL_UNCERTAIN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob dein Vorschlag gespeichert wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Schick ihn mit denselben Angaben noch einmal ab; doppelt angelegt wird dabei nichts.',
  retryable: true,
});

/** Freigeben, Umbenennen, Beenden, Verwerfen: lock_version laesst eine Wiederholung nichts doppelt tun. */
export const CHANGE_UNCERTAIN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob die Änderung gespeichert wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Lade den Rollenkatalog neu. Dann siehst du, was gilt.',
  retryable: true,
});

export const LOAD_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'LIAISON ist gerade nicht erreichbar.',
  explanation: 'Der Rollenkatalog ließ sich nicht laden.',
  nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
  retryable: true,
});

export const MORE_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'LIAISON ist gerade nicht erreichbar.',
  explanation: 'Die nächsten Rollen ließen sich nicht laden. Die bisherigen bleiben stehen.',
  nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
  retryable: true,
});

/** Der Katalog selbst ist nicht erreichbar (404 auf das Lesen). */
const CATALOG_GONE: PlainMessage = Object.freeze({
  headline: 'Der Rollenkatalog ist für dich gerade nicht erreichbar.',
  explanation: 'LIAISON hat ihn nicht gefunden. Es wurde nichts geändert.',
  nextStep: 'Lade die Seite neu. Bleibt es so, wende dich an den Betrieb.',
  retryable: true,
});

// Nach jedem Befehl laedt die Seite den Katalog selbst neu; die Saetze schicken
// deshalb nicht noch einmal zum Neuladen, sondern sagen, was jetzt zu tun ist.
const GONE: PlainMessage = Object.freeze({
  headline: 'Diese Rolle gibt es nicht mehr.',
  explanation: 'Sie wurde inzwischen entfernt. Es wurde nichts geändert.',
  nextStep: 'Die Liste ist jetzt aktuell; sie zeigt die Rolle nicht mehr.',
  retryable: false,
});

const OUTDATED: PlainMessage = Object.freeze({
  headline: 'Jemand hat diese Rolle gerade geändert.',
  explanation: 'Deine Freigabe wurde nicht ausgeführt, damit nichts Ungeprüftes vergebbar wird.',
  nextStep: 'Die Liste ist jetzt aktuell. Prüfe den Namen und gib die Rolle bei Bedarf erneut frei.',
  retryable: false,
});

export const ROLE_TYPE_MESSAGES: CommandMessages = Object.freeze({
  notFound: GONE,
  conflictFallback: OUTDATED,
  conflicts: Object.freeze({
    context_role_type_outdated: OUTDATED,
    context_role_type_name_taken: Object.freeze({
      headline: 'Eine Rolle mit diesem Namen gibt es schon.',
      explanation: 'Zwei gleichnamige Rollen wären an Kontakten nicht zu unterscheiden. Es wurde nichts angelegt.',
      nextStep: 'Nutze die vorhandene Rolle oder wähle einen Namen, der den Unterschied sagt.',
      retryable: false,
    }),
    context_role_type_second_person_required: Object.freeze({
      headline: 'Freigeben kann nur eine andere Person.',
      explanation: 'Du hast den Vorschlag oder den neuen Namen selbst gemacht. Es wurde nichts geändert.',
      nextStep: 'Bitte eine andere Person mit dem Recht für Stichworte, ihn freizugeben.',
      retryable: false,
    }),
    context_role_type_not_proposed: Object.freeze({
      headline: 'Diese Rolle ist schon freigegeben.',
      explanation: 'Sie ist bereits vergebbar; eine zweite Freigabe braucht es nicht.',
      nextStep: 'Die Liste ist jetzt aktuell.',
      retryable: false,
    }),
    context_role_type_already_ended: Object.freeze({
      headline: 'Diese Rolle ist schon beendet.',
      explanation: 'Sie wurde inzwischen beendet oder verworfen, vielleicht in einem anderen Fenster. Es wurde nichts geändert.',
      nextStep: 'Die Liste ist jetzt aktuell.',
      retryable: false,
    }),
    context_role_type_not_own_proposal: Object.freeze({
      headline: 'Einen Vorschlag benennt nur die Person um, die ihn gemacht hat.',
      explanation: 'Es wurde nichts geändert.',
      nextStep: 'Passt der Name nicht, verwirf den Vorschlag und schlag ihn neu vor.',
      retryable: false,
    }),
    context_role_type_name_unchanged: Object.freeze({
      headline: 'Die Rolle heißt schon so.',
      explanation: 'Der neue Name ist derselbe wie der bisherige. Es wurde nichts geändert.',
      nextStep: 'Gib einen anderen Namen ein oder brich ab.',
      retryable: false,
    }),
    context_role_type_seed_not_endable: Object.freeze({
      headline: 'Startrollen lassen sich nicht beenden.',
      explanation: 'Es wurde nichts geändert.',
      nextStep: 'Passt der Name nicht mehr, benenne die Rolle um.',
      retryable: false,
    }),
    client_request_reused: OUTDATED,
    client_request_superseded: Object.freeze({
      headline: 'Dein Vorschlag ist schon angekommen und wurde inzwischen bearbeitet.',
      explanation: 'Es wurde kein zweiter Vorschlag angelegt.',
      nextStep: 'Die Liste ist jetzt aktuell; dein Vorschlag steht darin.',
      retryable: false,
    }),
  }),
});

/** Das kurze Wort der Status-Pille; die Farbe kommt nur dazu (WCAG 1.4.1). */
export const STATUS_WORT: Readonly<Record<RoleTypeStatus, string>> = Object.freeze({
  proposed: 'Wartet auf Freigabe',
  active: 'Vergebbar',
  ended: 'Beendet',
});

/** Der einzige Erklaersatz der Seite (DESIGN 14.10.): Geltung und was die Zahl zaehlt. */
export const CATALOG_NOTE = 'Rollen gelten in der ganzen Gruppe. Die Zahl zählt Kontakte, die du sehen darfst.';

/**
 * Einmal im Kopf, wenn eine aktive Rolle weder umbenannt noch beendet werden darf: sonst stuende
 * die Zeile ohne Knopf und ohne Grund da. Startrollen tragen bei Schalter aus canRename, der Satz
 * erscheint dann nie; eine eigene Kennung fuer Startrollen braucht es dafuer nicht.
 */
export const STEWARD_CHANGE_NOTE = 'Ändern kann jemand mit dem Recht „Datenpflege“.';

export function needsStewardNote(rollen: readonly Pick<RoleTypeView, 'typeStatus' | 'canRename' | 'canEnd'>[]): boolean {
  return rollen.some((rolle) => rolle.typeStatus === 'active' && !rolle.canRename && !rolle.canEnd);
}

/** Der Pruefsatz vor der Freigabe (F3, Art. 9 DSGVO); das Kaestchen sendet confirmsNoSpecialCategory. */
export function specialCategorySentence(name: string): string {
  return `„${name}“ beschreibt, was jemand für einen Verlag, eine Holding oder einen Vertrieb tut – kein Merkmal wie Gesundheit, Herkunft, Religion, Weltanschauung, politische Haltung oder Gewerkschaft.`;
}

/** Die Zweitzeile am eigenen Vorschlag: kein Knopf, aber der Grund (Vier Augen). */
export const OWN_PROPOSAL_NOTE = 'Freigeben kann nur eine andere Person.';
/** Am eigenen Vorschlag, wenn die Seite das fehlende Recht schon nennt (needsStewardNote): wer freigibt, braucht es. */
export const OWN_PROPOSAL_STEWARD_NOTE = 'Freigeben kann jemand anderes mit dem Recht „Datenpflege“.';

export function appliedSentence(action: 'propose' | 'approve', name: string): string {
  return action === 'propose'
    ? `„${name}“ ist vorgeschlagen. Vergebbar wird die Rolle, sobald eine andere Person sie freigibt.`
    : `„${name}“ ist freigegeben und lässt sich jetzt an Kontakten vergeben.`;
}

const STATUS = new Set<string>(['proposed', 'active', 'ended']);

function asText(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function roleTypeOf(row: Record<string, unknown>): RoleTypeView | undefined {
  if (
    typeof row.roleCode !== 'string' ||
    typeof row.name !== 'string' ||
    typeof row.lockVersion !== 'number' ||
    typeof row.typeStatus !== 'string' ||
    !STATUS.has(row.typeStatus)
  ) {
    return undefined;
  }
  return {
    roleCode: row.roleCode,
    name: row.name,
    approvedName: asText(row.approvedName),
    description: asText(row.description),
    typeStatus: row.typeStatus as RoleTypeStatus,
    lockVersion: row.lockVersion,
    proposedByMe: row.proposedByMe === true,
    assignmentCount: typeof row.assignmentCount === 'number' ? row.assignmentCount : 0,
    // Fehlt ein Recht in der Antwort, gibt es den Knopf nicht: das Web leitet keine Rechte ab.
    canApprove: row.canApprove === true,
    canRename: row.canRename === true,
    canEnd: row.canEnd === true,
  };
}

export async function fetchRoleTypes(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly cursor?: string;
  readonly send?: SendLike;
}): Promise<RoleTypePageResult> {
  const query = options.cursor === undefined ? '' : `?cursor=${encodeURIComponent(options.cursor)}`;
  const result = await readResource({
    url: `${options.baseUrl}/context-role-types${query}`,
    accessToken: options.accessToken,
    ...(options.send ? { send: options.send } : {}),
  });
  if (result.outcome === 'not-found') return { outcome: 'rejected', message: CATALOG_GONE };
  if (result.outcome === 'rejected') return result;
  const body = result.body;
  const roleTypes = Array.isArray(body.roleTypes)
    ? body.roleTypes.flatMap((row: unknown) => roleTypeOf((row ?? {}) as Record<string, unknown>) ?? [])
    : [];
  return { outcome: 'loaded', page: { roleTypes, nextCursor: typeof body.nextCursor === 'string' ? body.nextCursor : undefined } };
}

/**
 * Haengt eine weitere Seite an. Eine Rolle, die zwischen zwei Seiten den Rang
 * gewechselt hat (etwa freigegeben), stuende sonst zweimal da; es gilt die
 * juengere Fassung an der alten Stelle.
 */
export function appendPage(bisher: readonly RoleTypeView[], neu: readonly RoleTypeView[]): readonly RoleTypeView[] {
  const juenger = new Map(neu.map((rolle) => [rolle.roleCode, rolle]));
  const ersetzt = bisher.map((rolle) => juenger.get(rolle.roleCode) ?? rolle);
  const bekannt = new Set(bisher.map((rolle) => rolle.roleCode));
  return [...ersetzt, ...neu.filter((rolle) => !bekannt.has(rolle.roleCode))];
}

export type RoleTypeCommandResult = CommandOutcome<{ readonly typeStatus: string }>;

const applied = (record: Record<string, unknown>) => ({ typeStatus: asText(record.typeStatus) ?? '' });

export function proposeRoleType(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly input: { readonly name: string; readonly description: string };
  readonly clientRequestId: string;
  readonly send?: SendLike;
}): Promise<RoleTypeCommandResult> {
  const description = options.input.description.trim();
  return sendCommand({
    url: `${options.baseUrl}/context-role-types`,
    accessToken: options.accessToken,
    body: {
      name: options.input.name.trim(),
      ...(description === '' ? {} : { description }),
      clientRequestId: options.clientRequestId,
    },
    messages: ROLE_TYPE_MESSAGES,
    applied,
    ...(options.send ? { send: options.send } : {}),
  });
}

/**
 * `expectedName` bindet die Freigabe an den Namen, den die Person geprueft
 * hat: wurde er inzwischen geaendert, lehnt der Server mit _outdated ab.
 */
export function approveRoleType(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly roleType: RoleTypeView;
  /** Die Person hat den Pruefsatz (Art. 9 DSGVO) selbst bestaetigt; ohne das gibt es keinen Aufruf. */
  readonly confirmsNoSpecialCategory: true;
  readonly send?: SendLike;
}): Promise<RoleTypeCommandResult> {
  return sendCommand({
    url: `${options.baseUrl}/context-role-types/${encodeURIComponent(options.roleType.roleCode)}/approve`,
    accessToken: options.accessToken,
    body: { lockVersion: options.roleType.lockVersion, expectedName: options.roleType.name, confirmsNoSpecialCategory: options.confirmsNoSpecialCategory },
    messages: roleTypeMessages('approve'),
    applied,
    ...(options.send ? { send: options.send } : {}),
  });
}

// --- Schnitt 2: umbenennen, beenden, verwerfen ---------------------------

/**
 * Ein neuer Name wartet auf Freigabe: die Rolle ist vergebbar, an Kontakten
 * steht aber bis zur Freigabe der zuletzt freigegebene Name (E7').
 */
export function renamePending(rolle: Pick<RoleTypeView, 'typeStatus' | 'name' | 'approvedName'>): boolean {
  return rolle.typeStatus === 'active' && rolle.approvedName !== null && rolle.name !== rolle.approvedName;
}

/** Am Feld, wenn der neue Name dem alten gleicht; dann wird nicht gesendet. */
export const SAME_NAME_FIELD = 'Der neue Name ist derselbe wie der bisherige.';

/** Die Zweitzeile einer umbenannten Rolle bis zur Freigabe. */
export function pendingRenameNote(approvedName: string): string {
  return `An Kontakten heißt sie bis zur Freigabe „${approvedName}“.`;
}

/** Was das Beenden bewirkt; steht in der Rueckfrage vor dem Bestaetigen. */
/** Die Zahl an der Zeile (DESIGN zu B3): bei 0 kein "0 Kontakte", sondern was es heisst. */
export function assignmentSentence(count: number): string {
  if (count === 0) return 'Noch keinem Kontakt zugeordnet';
  return count === 1 ? '1 Kontakt' : `${count.toLocaleString('de-DE')} Kontakte`;
}

export const END_EFFECT = 'Bestehende Zuordnungen bleiben sichtbar; neu vergeben lässt sich die Rolle nicht mehr.';

/** Was das Verwerfen bzw. Zurueckziehen eines Vorschlags bewirkt. */
export const DISCARD_EFFECT = 'Der Vorschlag wird nicht vergebbar. Gelöscht wird nichts.';

export type RoleTypeAction = 'approve' | 'rename' | 'end' | 'discard' | 'withdraw';

const NICHT_AUSGEFUEHRT: Readonly<Record<Exclude<RoleTypeAction, 'approve'>, string>> = Object.freeze({
  rename: 'Dein Umbenennen wurde nicht ausgeführt.',
  end: 'Dein Beenden wurde nicht ausgeführt.',
  discard: 'Dein Verwerfen wurde nicht ausgeführt.',
  withdraw: 'Dein Zurückziehen wurde nicht ausgeführt.',
});

/**
 * Freigeben, Umbenennen und Beenden verlangen bei LIAISON_CATALOG_ROLE_ENFORCED das Recht
 * "Datenpflege"; ein 403 heisst, der Schalter wurde nach dem Laden umgelegt. Der allgemeine
 * Satz spraeche von einer "Ansicht". Wie die Geschwister sagt er "jetzt aktuell": die Seite
 * laedt nach jedem Befehl neu, und scheitert das, ersetzt sie den naechsten Schritt.
 */
export const MAINTAIN_FORBIDDEN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Das darf nur jemand mit dem Recht „Datenpflege“.',
  explanation: 'Deine Änderung wurde nicht gespeichert.',
  nextStep: 'Die Liste ist jetzt aktuell und zeigt, was du hier tun kannst.',
  retryable: false,
});

/**
 * Die Saetze einer Aktion: "jemand war schneller" sagt, was nicht geschah.
 * Die Freigabe behaelt ihren eigenen Satz (nichts Ungeprueftes wird vergebbar).
 * Beim Umbenennen nennt der Satz den getippten Namen: die Rueckfrage schliesst,
 * und sonst stuende er nirgends mehr (DESIGN #685).
 */
export function roleTypeMessages(action: RoleTypeAction, neuerName?: string): CommandMessages {
  if (action === 'approve') {
    return Object.freeze({
      ...ROLE_TYPE_MESSAGES,
      forbidden: MAINTAIN_FORBIDDEN_MESSAGE,
      conflicts: Object.freeze({
        ...ROLE_TYPE_MESSAGES.conflicts,
        // Der freizugebende Name ist inzwischen an eine andere Rolle vergeben.
        context_role_type_name_taken: Object.freeze({
          headline: 'Eine Rolle mit diesem Namen gibt es schon.',
          explanation: 'Deine Freigabe wurde nicht ausgeführt.',
          nextStep: 'Benenne die Rolle um und lass sie danach freigeben.',
          retryable: false,
        }),
      }),
    });
  }
  const outdated: PlainMessage = Object.freeze({
    headline: OUTDATED.headline,
    explanation: action === 'rename' && neuerName !== undefined ? `Dein Umbenennen in „${neuerName}“ wurde nicht ausgeführt.` : NICHT_AUSGEFUEHRT[action],
    nextStep: 'Die Liste ist jetzt aktuell. Prüfe die Rolle und versuche es bei Bedarf erneut.',
    retryable: false,
  });
  return Object.freeze({
    ...ROLE_TYPE_MESSAGES,
    forbidden: MAINTAIN_FORBIDDEN_MESSAGE,
    conflictFallback: outdated,
    conflicts: Object.freeze({ ...ROLE_TYPE_MESSAGES.conflicts, context_role_type_outdated: outdated, client_request_reused: outdated }),
  });
}

/**
 * Statt "Freigeben", wenn der Server es dieser Person verweigert, obwohl etwas
 * auf Freigabe wartet. Am fremden Vorschlag fehlt das Recht "Datenpflege"; am
 * wartenden Namen sagt der Server nicht, ob die Person ihn selbst vergeben hat
 * (vier Augen) oder ob das Recht fehlt, also nennt der Satz beides.
 */
export const STEWARD_NOTE = 'Freigeben kann jemand mit dem Recht „Datenpflege“.';
export const OWN_NAME_NOTE = 'Freigeben kann jemand mit dem Recht „Datenpflege“, der den Namen nicht selbst vergeben hat.';

/** Die Zweitzeile ohne Freigabeknopf: eigener Vorschlag, oder Name selbst vergeben; sonst keine. */
export function approvalNote(
  rolle: Pick<RoleTypeView, 'typeStatus' | 'name' | 'approvedName' | 'proposedByMe' | 'canApprove'>,
  ohneRecht = false,
): string | undefined {
  if (rolle.canApprove) return undefined;
  if (rolle.typeStatus === 'proposed') return rolle.proposedByMe ? (ohneRecht ? OWN_PROPOSAL_STEWARD_NOTE : OWN_PROPOSAL_NOTE) : STEWARD_NOTE;
  return renamePending(rolle) ? OWN_NAME_NOTE : undefined;
}

/**
 * Der Satz nach dem Umbenennen: am Vorschlag, an einer vergebbaren Rolle, beim
 * Ersetzen eines wartenden Namens und beim Zurueck auf den freigegebenen Namen.
 */
export function renamedSentence(rolle: Pick<RoleTypeView, 'typeStatus' | 'name' | 'approvedName'>, neuerName: string): string {
  if (rolle.typeStatus === 'proposed' || rolle.approvedName === null) {
    return `Der Vorschlag heißt jetzt „${neuerName}“. Vergebbar wird er, sobald eine andere Person ihn freigibt.`;
  }
  if (neuerName === rolle.approvedName) return `„${rolle.approvedName}“ bleibt der Name. Der wartende Name „${rolle.name}“ ist zurückgezogen.`;
  if (renamePending(rolle)) return `Der neue Name „${neuerName}“ wartet auf Freigabe. An Kontakten heißt die Rolle weiter „${rolle.approvedName}“.`;
  return `„${rolle.name}“ heißt jetzt „${neuerName}“. An Kontakten gilt der neue Name, sobald eine andere Person ihn freigibt.`;
}

export function appliedSentenceS2(action: 'end' | 'discard' | 'withdraw', name: string): string {
  if (action === 'discard') return `Der Vorschlag „${name}“ ist verworfen.`;
  if (action === 'withdraw') return `Dein Vorschlag „${name}“ ist zurückgezogen.`;
  return `„${name}“ ist beendet. Bestehende Zuordnungen bleiben sichtbar.`;
}

export function renameRoleType(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly roleType: RoleTypeView;
  readonly name: string;
  readonly send?: SendLike;
}): Promise<RoleTypeCommandResult> {
  return sendCommand({
    url: `${options.baseUrl}/context-role-types/${encodeURIComponent(options.roleType.roleCode)}/rename`,
    accessToken: options.accessToken,
    body: { name: options.name.trim(), lockVersion: options.roleType.lockVersion },
    messages: roleTypeMessages('rename', options.name.trim()),
    applied,
    ...(options.send ? { send: options.send } : {}),
  });
}

/**
 * Beendet eine vergebbare Rolle (Grund obsolete) oder verwirft einen Vorschlag
 * (Grund created_in_error). Den Grund waehlt niemand von Hand.
 */
export function endRoleType(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly roleType: RoleTypeView;
  readonly send?: SendLike;
}): Promise<RoleTypeCommandResult> {
  const vorschlag = options.roleType.typeStatus === 'proposed';
  return sendCommand({
    url: `${options.baseUrl}/context-role-types/${encodeURIComponent(options.roleType.roleCode)}/end`,
    accessToken: options.accessToken,
    body: { lockVersion: options.roleType.lockVersion, endReasonCode: vorschlag ? 'created_in_error' : 'obsolete' },
    messages: roleTypeMessages(vorschlag ? (options.roleType.proposedByMe ? 'withdraw' : 'discard') : 'end'),
    applied,
    ...(options.send ? { send: options.send } : {}),
  });
}
